import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import api from './api/axios';
import { Menu, X, LogOut, Users, BarChart4, TrendingUp, Mail } from 'lucide-react';
import RequestAccess from './RequestAccess';
import AccountSummary from './AccountSummary';
import BalanceAnalytics from './BalanceAnalytics';
import EmailHistory from './EmailHistory';

const Dashboard = () => {
  const [userData, setUserData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenuItem, setActiveMenuItem] = useState('requestAccess');
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  
  // Define menu items with icons
  const menuItems = [
    { id: 'requestAccess', label: 'Request Access', icon: <Users className="w-5 h-5" /> },
    { id: 'accountSummary', label: 'Account Summary', icon: <BarChart4 className="w-5 h-5" /> },
    { id: 'balanceAnalytics', label: 'Balance Analytics', icon: <TrendingUp className="w-5 h-5" /> },
    { id: 'emailHistory', label: 'Email History', icon: <Mail className="w-5 h-5" /> }
  ];
  
  // Format user name from userData
  const getUserName = () => {
    if (!userData) return 'User';
    
    if (userData.full_name) {
      return userData.full_name;
    }
    
    // Fall back to email or username if name isn't available
    return userData.email || userData.username || 'User';
  };
  
  useEffect(() => {
    // Function to execute as soon as component mounts
    const checkForToken = async () => {
      
      // Check for token in URL
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (token) {
        localStorage.setItem('token', token);
        setIsAuthenticated(true);
        
        // Remove token from URL for security
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Try to fetch user data with the token
        try {
          const response = await api.get('/api/user/profile');
          
          setUserData(response.data);
          setIsLoading(false);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setIsLoading(false);
        }
      } else {
        // Check localStorage
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          setIsAuthenticated(true);
          
          // Try to fetch user data with the stored token
          try {
            const response = await api.get('/api/user/profile');
            
              setUserData(response.data);
            setIsLoading(false);
          } catch (error) {
            console.error('Error fetching user data:', error);
            
            // If unauthorized, clear token and redirect to login
            if (error.response && error.response.status === 401) {
              localStorage.removeItem('token');
              setIsAuthenticated(false);
              setIsLoading(false);
              navigate('/login');
            } else {
              setIsLoading(false);
            }
          }
        } else {
          setIsAuthenticated(false);
          setIsLoading(false);
          // Redirect to login if no token exists
          navigate('/login');
        }
      }
    };
    
    // Run immediately
    checkForToken();
  }, [navigate]);
  
  const handleLogout = () => {
    // Clear authentication state
    localStorage.removeItem('token');
    setUserData(null);
    setIsAuthenticated(false);
    
    // Force a complete page reload to ensure clean state
    window.location.href = '/login';
  };

  // Loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // If not authenticated, don't render anything as we'll be redirected
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed md:relative z-10 md:z-0 w-64 h-screen bg-brand-700 text-white border-r border-brand-600 transition-transform duration-300 ease-in-out md:translate-x-0`}>
        <div className="p-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img src="/cartiga.svg" alt="Cartiga" className="w-8 h-8" />
              <h1 className="text-lg font-bold text-white ml-2">Plaid</h1>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="md:hidden text-white/70 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="mt-6 mb-8">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
                <span className="text-sm font-semibold text-white">
                  {getUserName().split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="ml-3">
                <p className="font-medium text-white">{getUserName()}</p>
                <p className="text-xs text-white/60">{userData?.email || 'No email'}</p>
              </div>
            </div>
          </div>
        </div>

        <nav className="mt-2">
          <ul>
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => setActiveMenuItem(item.id)}
                  className={`flex items-center w-full px-4 py-2.5 mx-3 mb-1 text-left transition-all duration-200 ${
                    activeMenuItem === item.id
                      ? 'bg-white/15 rounded-xl text-white'
                      : 'hover:bg-white/10 rounded-xl text-white/70'
                  }`}
                >
                  <span className={activeMenuItem === item.id ? 'text-white' : 'text-white/50'}>
                    {item.icon}
                  </span>
                  <span className="ml-3">{item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="absolute bottom-0 w-full p-4">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2.5 mx-0 text-white/60 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200"
          >
            <LogOut className="w-5 h-5" />
            <span className="ml-3">Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Content Area */}
        <main className="flex-1 overflow-y-auto bg-gray-50 p-4">
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden fixed top-4 left-4 z-20 p-2 bg-white rounded-full shadow-md text-gray-600 hover:text-gray-800 transition-all duration-200"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="container mx-auto">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 md:mb-5">{menuItems.find(item => item.id === activeMenuItem)?.label || 'Dashboard'}</h2>
            {activeMenuItem === 'requestAccess' && <RequestAccess />}
            {activeMenuItem === 'accountSummary' && <AccountSummary />}
            {activeMenuItem === 'balanceAnalytics' && <BalanceAnalytics />}
            {activeMenuItem === 'emailHistory' && <EmailHistory />}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;